import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Quel est le prix pour motoriser un volet roulant manuel à Lyon ?', a: 'Le coût dépend du type de moteur choisi (filaire ou radio), du nombre de volets à motoriser, et de l\'accessibilité du caisson. Nous vous fournissons un devis gratuit et détaillé après examen de votre installation. Comptez généralement entre 250 € et 450 € par volet, motorisation et commande incluses.' },
  { q: 'Peut-on motoriser un volet roulant sans remplacer le tablier ?', a: 'Oui, dans la grande majorité des cas, la motorisation se fait en conservant le tablier existant. Nous remplaçons uniquement le mécanisme d\'enroulement (treuil ou sangle) par un moteur adapté. Si vos lames sont en bon état, il n\'y a aucune raison de les changer.' },
  { q: 'Combien de temps dure la motorisation d\'un volet roulant ?', a: 'L\'intervention prend environ 1 à 2 heures par volet. Nous intervenons sur Lyon et la métropole, et notre technicien repart une fois les tests de fonctionnement effectués et la télécommande programmée.' },
];

export default function Motorisation() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Motorisation de volet roulant à Lyon | DFPRO"
        description="Passez du volet manuel au volet électrique à Lyon. Motorisation filaire ou radio, télécommande, domotique. Devis gratuit, garantie 10 ans. 09 80 80 36 84."
        canonicalUrl="https://depanfenetres-lyon.fr/motorisation-volet-roulant-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-motorisation-2026.webp" alt="Motorisation de volet roulant à Lyon par DFPRO Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Motorisation</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Motorisation de volet roulant à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Envie de passer du volet manuel au volet électrique ? Nos techniciens motorisent votre volet roulant à Lyon. Confort, sécurité et modernité au quotidien.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Pourquoi motoriser son volet roulant manuel ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Si vous avez encore des volets roulants manuels à sangle ou à manivelle, vous savez à quel point leur manipulation peut être contraignante au quotidien. Tirer sur une sangle récalcitrante ou tourner une manivelle peu accessible, plusieurs fois par jour, ce n'est pas l'idée que l'on se fait du confort moderne.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    La motorisation apporte un confort immédiat : une simple pression sur un interrupteur mural, une télécommande, ou même votre smartphone (avec un module domotique compatible) suffit pour ouvrir ou fermer tous vos volets. Finis les efforts, les sangles qui claquent et les manivelles qui grincent.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Au-delà du confort, la motorisation améliore aussi la sécurité de votre habitation. Vous pouvez programmer la fermeture automatique de vos volets le soir, même si vous n'êtes pas chez vous. Certains systèmes permettent même de simuler une présence en ouvrant et fermant les volets à des heures aléatoires.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Motorisation filaire ou radio : que choisir ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Deux technologies s'offrent à vous. La motorisation filaire est la solution la plus classique : le moteur est relié à un interrupteur mural par un câble électrique. C'est une solution fiable, économique, et qui ne nécessite ni pile ni programmation complexe.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    La motorisation radio (sans fil) offre davantage de possibilités : télécommande individuelle ou centralisée, programmation horaire, intégration domotique (Somfy TaHoma, Google Home, Alexa). Vous pilotez vos volets depuis votre canapé ou à distance. C'est la solution la plus confortable et la plus évolutive.
                  </p>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/reparation-moteur-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation moteur</Link>
                    <Link to="/volet-roulant-electrique-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet électrique</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — motorisation</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Prêt à motoriser vos volets roulants à Lyon ?" subtitle="Confort, sécurité, modernité. Devis gratuit et sans engagement." />
      </main>
      <Footer />
    </>
  );
}